import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Plus,
  Edit,
  Trash2,
  Eye,
  Search,
  Filter,
  Package,
  Image,
  DollarSign,
  Scale,
} from "lucide-react";
import {
  getProducts,
  createProduct,
  updateProduct,
  deleteProduct,
  getAllProducts,
} from "../lib/pocketbase";
import { getAllCategories } from "../lib/pocketbase";
import { getProductImageUrl } from "../lib/config";
import { Product, ProductForm, Category } from "../types";
import NestedSelect from "../components/ui/NestedSelect";
import Input from "../components/ui/Input";
import Select from "../components/ui/Select";
import Textarea from "../components/ui/Textarea";
import FileUpload from "../components/ui/FileUpload";
import {
  Table,
  TableHeader,
  TableRow,
  TableCell,
  TableHeaderCell,
} from "../components/ui/Table";
import CategoryPath from "../components/ui/CategoryPath";
import { formatCurrency, formatPersianDate } from "../lib/utils";

const ProductsPage: React.FC = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [perPage] = useState(12);
  const [searchTerm, setSearchTerm] = useState("");
  const [apiSearchTerm, setApiSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [lastSelectedCategoryId, setLastSelectedCategoryId] =
    useState<string>("");
  const [lastSelectedType, setLastSelectedType] = useState<"2d" | "3d">("2d");

  const [formData, setFormData] = useState<ProductForm>({
    title: "",
    title_en: "",
    caption: "",
    weight: 0,
    price: 0,
    make_price: 0,
    segment: "gallery",
    type: lastSelectedType,
    category_id: lastSelectedCategoryId,
  });

  const [formFiles, setFormFiles] = useState<{
    avatar: File[];
    photos: File[];
  }>({
    avatar: [],
    photos: [],
  });

  useEffect(() => {
    fetchProducts();
  }, [currentPage, selectedCategory, apiSearchTerm]);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchProducts = useCallback(async () => {
    try {
      const response = await getProducts(
        currentPage,
        perPage,
        selectedCategory || undefined,
        apiSearchTerm || undefined
      );
      setProducts(response.items);
      setTotalPages(response.totalPages);
    } catch (error: any) {
      if (error?.message?.includes("autocancelled")) {
        return;
      }
      console.error("Error fetching products:", error);
    } finally {
      setLoading(false);
    }
  }, [currentPage, perPage, selectedCategory, apiSearchTerm]);

  const fetchCategories = useCallback(async () => {
    try {
      const data = await getAllCategories();
      setCategories(data);
    } catch (error: any) {
      if (error?.message?.includes("autocancelled")) {
        return;
      }
      console.error("Error fetching categories:", error);
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const formDataObj = new FormData();
      formDataObj.append("title", formData.title);
      formDataObj.append("title_en", formData.title_en);
      formDataObj.append("caption", formData.caption || "");
      formDataObj.append("weight", formData.weight?.toString() || "0");
      formDataObj.append("price", formData.price?.toString() || "0");
      formDataObj.append("make_price", formData.make_price?.toString() || "0");
      formDataObj.append("segment", "gallery");
      formDataObj.append("type", formData.type || "2d");

      if (formData.category_id) {
        formDataObj.append("category_id", formData.category_id);
      }

      if (formFiles.avatar.length > 0) {
        formDataObj.append("avatar", formFiles.avatar[0]);
      }

      // Handle photos differently for create vs update
      if (editingProduct) {
        // For updates: append new photos to existing ones
        if (formFiles.photos.length > 0) {
          // Add existing photos to FormData first
          if (editingProduct.photos && editingProduct.photos.length > 0) {
            editingProduct.photos.forEach((photo) => {
              formDataObj.append("photos", photo);
            });
          }

          // Then add new photos
          formFiles.photos.forEach((photo) => {
            formDataObj.append("photos", photo);
          });
        }
      } else {
        // For new products: just add the new photos
        if (formFiles.photos.length > 0) {
          formFiles.photos.forEach((photo) => {
            formDataObj.append("photos", photo);
          });
        }
      }

      if (editingProduct) {
        await updateProduct(editingProduct.id, formDataObj);
      } else {
        await createProduct(formDataObj);
      }

      setShowModal(false);
      setEditingProduct(null);
      resetForm();
      fetchProducts();
    } catch (error) {
      console.error("Error saving product:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (product: Product) => {
    setEditingProduct(product);
    setFormData({
      title: product.title,
      title_en: product.title_en,
      caption: product.caption || "",
      weight: product.weight || 0,
      price: product.price || 0,
      make_price: product.make_price || 0,
      segment: "gallery",
      type: product.type || "2d",
      category_id: product.expand?.category_id?.id || "",
    });
    setFormFiles({
      avatar: [],
      photos: [],
    });
    setShowModal(true);
  };

  const handleDelete = async (id: string) => {
    if (window.confirm("آیا از حذف این محصول اطمینان دارید؟")) {
      try {
        await deleteProduct(id);
        fetchProducts();
      } catch (error) {
        console.error("Error deleting product:", error);
      }
    }
  };

  const handleRemovePhoto = async (photoFilename: string) => {
    if (!editingProduct) return;

    try {
      const updatedPhotos =
        editingProduct.photos?.filter((p) => p !== photoFilename) || [];
      await updateProduct(editingProduct.id, { photos: updatedPhotos });

      // Refresh product data
      setEditingProduct((prev) =>
        prev ? { ...prev, photos: updatedPhotos } : null
      );
      fetchProducts(); // To update the main list
    } catch (error) {
      console.error("Error removing photo:", error);
    }
  };

  const resetForm = () => {
    setFormData({
      title: "",
      title_en: "",
      caption: "",
      weight: 0,
      price: 0,
      make_price: 0,
      segment: "gallery",
      type: lastSelectedType,
      category_id: lastSelectedCategoryId,
    });
    setFormFiles({
      avatar: [],
      photos: [],
    });
  };

  // Debounced search function
  const handleSearchChange = (value: string) => {
    setSearchTerm(value);

    // Clear existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Set new timeout for API call
    searchTimeoutRef.current = setTimeout(() => {
      setApiSearchTerm(value);
    }, 500); // Wait 500ms after user stops typing
  };

  const filteredProducts = products;

  const typeOptions = [
    { value: "2d", label: "2D" },
    { value: "3d", label: "3D" },
  ];

  if (loading && products.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:justify-between sm:items-center">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">مدیریت محصولات</h1>
          <p className="text-gray-600">ایجاد و مدیریت محصولات</p>
        </div>
        <button
          onClick={() => {
            setEditingProduct(null);
            resetForm();
            setShowModal(true);
          }}
          className="btn-primary flex items-center justify-center w-full sm:w-auto"
        >
          <Plus className="h-4 w-4 ml-2" />
          افزودن محصول
        </button>
      </div>

      {/* Filters */}
      <div className="card p-6">
        <div className="flex items-center mb-4">
          <Filter className="h-5 w-5 text-gray-500 ml-2" />
          <h3 className="text-lg font-medium text-gray-900">فیلترها</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Input
              label="جستجو"
              value={searchTerm}
              onChange={(e) => handleSearchChange(e.target.value)}
              placeholder="جستجو در محصولات..."
              helperText="جستجو بر اساس عنوان فارسی یا انگلیسی (500ms تاخیر)"
            />
          </div>

          <div>
            <NestedSelect
              label="دسته‌بندی"
              value={selectedCategory}
              onChange={setSelectedCategory}
              options={categories}
              placeholder="همه دسته‌بندی‌ها"
            />
          </div>

          <div className="">
            <button
              onClick={() => {
                setSearchTerm("");
                setApiSearchTerm("");
                setSelectedCategory("");
              }}
              className="btn-secondary w-full"
            >
              پاک کردن فیلترها
            </button>
          </div>
        </div>
      </div>

      {/* Products Table */}
      <div className="card">
        <Table>
          <TableHeader>
            <TableHeaderCell>تصویر</TableHeaderCell>
            <TableHeaderCell>عنوان</TableHeaderCell>
            <TableHeaderCell>دسته‌بندی</TableHeaderCell>
            <TableHeaderCell>قیمت</TableHeaderCell>
            <TableHeaderCell>وزن</TableHeaderCell>
            <TableHeaderCell>نوع</TableHeaderCell>
            <TableHeaderCell>تاریخ ایجاد</TableHeaderCell>
            <TableHeaderCell>عملیات</TableHeaderCell>
          </TableHeader>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredProducts.map((product) => (
              <TableRow key={product.id}>
                <TableCell>
                  <div className="flex items-center">
                    <div className="w-12 h-12 bg-gray-100 rounded-lg overflow-hidden">
                      {product.avatar ? (
                        <img
                          src={getProductImageUrl(product.id, product.avatar)}
                          alt={product.title}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Image className="h-6 w-6 text-gray-400" />
                        </div>
                      )}
                    </div>
                  </div>
                </TableCell>
                <TableCell>
                  <div>
                    <div className="font-medium text-gray-900">
                      {product.title}
                    </div>
                    <div className="text-sm text-gray-500">
                      {product.title_en}
                    </div>
                  </div>
                </TableCell>
                <TableCell>
                  {product.expand?.category_id ? (
                    <CategoryPath
                      category={product.expand.category_id}
                      allCategories={categories}
                      showIcons={false}
                      maxDepth={3}
                    />
                  ) : (
                    <span className="text-gray-400">بدون دسته‌بندی</span>
                  )}
                </TableCell>
                <TableCell>
                  {product.price ? (
                    <span className="text-green-600 font-medium">
                      {formatCurrency(product.price)}
                    </span>
                  ) : (
                    <span className="text-gray-400">-</span>
                  )}
                </TableCell>
                <TableCell>
                  {product.weight ? (
                    <span className="text-gray-600">{product.weight} گرم</span>
                  ) : (
                    <span className="text-gray-400">-</span>
                  )}
                </TableCell>
                <TableCell>
                  <span className="text-sm text-gray-600">
                    {product.type?.toUpperCase() || "-"}
                  </span>
                </TableCell>
                <TableCell>
                  <span className="text-sm text-gray-500">
                    {formatPersianDate(product.created)}
                  </span>
                </TableCell>
                <TableCell>
                  <div className="flex items-center space-x-2 space-x-reverse">
                    <button
                      onClick={() => handleEdit(product)}
                      className="p-1 text-primary-600 hover:text-primary-700 transition-colors"
                      title="ویرایش"
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(product.id)}
                      className="p-1 text-red-600 hover:text-red-700 transition-colors"
                      title="حذف"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </tbody>
        </Table>
      </div>

      {/* Empty State */}
      {filteredProducts.length === 0 && !loading && (
        <div className="text-center py-12">
          <Package className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">
            هیچ محصولی یافت نشد
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            {searchTerm || selectedCategory
              ? "فیلترهای خود را تغییر دهید یا محصول جدیدی اضافه کنید"
              : "شروع به ایجاد محصول کنید"}
          </p>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center overflow-x-auto">
          <nav className="flex items-center space-x-2 space-x-reverse">
            <button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className="btn-secondary disabled:opacity-50"
            >
              قبلی
            </button>

            {(() => {
              const pages = [];
              const maxVisiblePages = 7; // Show max 7 page buttons

              if (totalPages <= maxVisiblePages) {
                // Show all pages if total is small
                for (let i = 1; i <= totalPages; i++) {
                  pages.push(i);
                }
              } else {
                // Show first 3 pages
                for (let i = 1; i <= 3; i++) {
                  pages.push(i);
                }

                // Add ellipsis if current page is not near the beginning
                if (currentPage > 4) {
                  pages.push("...");
                }

                // Show current page and neighbors
                const start = Math.max(4, currentPage - 1);
                const end = Math.min(totalPages - 3, currentPage + 1);

                for (let i = start; i <= end; i++) {
                  if (!pages.includes(i)) {
                    pages.push(i);
                  }
                }

                // Add ellipsis if current page is not near the end
                if (currentPage < totalPages - 3) {
                  pages.push("...");
                }

                // Show last 3 pages
                for (let i = totalPages - 2; i <= totalPages; i++) {
                  if (!pages.includes(i)) {
                    pages.push(i);
                  }
                }
              }

              return pages.map((page, index) => (
                <button
                  key={index}
                  onClick={() =>
                    typeof page === "number" && setCurrentPage(page)
                  }
                  disabled={page === "..."}
                  className={`px-3 py-2 text-sm font-medium rounded-lg ${
                    page === currentPage
                      ? "bg-primary-600 text-white"
                      : page === "..."
                      ? "bg-white text-gray-400 cursor-default"
                      : "bg-white text-gray-700 hover:bg-gray-50 border border-gray-300"
                  }`}
                >
                  {page}
                </button>
              ));
            })()}

            <button
              onClick={() =>
                setCurrentPage(Math.min(totalPages, currentPage + 1))
              }
              disabled={currentPage === totalPages}
              className="btn-secondary disabled:opacity-50"
            >
              بعدی
            </button>
          </nav>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-4 sm:top-20 mx-auto p-4 sm:p-5 border w-[calc(100%-2rem)] max-w-4xl shadow-lg rounded-md bg-white max-h-[90vh] overflow-y-auto">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {editingProduct ? "ویرایش محصول" : "افزودن محصول جدید"}
              </h3>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Input
                    label="عنوان (فارسی)"
                    value={formData.title}
                    onChange={(e) =>
                      setFormData({ ...formData, title: e.target.value })
                    }
                    required
                  />

                  <Input
                    label="عنوان (انگلیسی)"
                    value={formData.title_en}
                    onChange={(e) =>
                      setFormData({ ...formData, title_en: e.target.value })
                    }
                    required
                  />
                </div>

                <Textarea
                  label="توضیحات"
                  value={formData.caption}
                  onChange={(e) =>
                    setFormData({ ...formData, caption: e.target.value })
                  }
                  placeholder="توضیحات محصول..."
                />

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <Input
                    label="وزن (گرم)"
                    type="number"
                    value={formData.weight}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        weight: parseFloat(e.target.value) || 0,
                      })
                    }
                  />

                  <Input
                    label="قیمت (تومان)"
                    type="number"
                    value={formData.price}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        price: parseFloat(e.target.value) || 0,
                      })
                    }
                  />

                  <Input
                    label="قیمت ساخت (تومان)"
                    type="number"
                    value={formData.make_price}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        make_price: parseFloat(e.target.value) || 0,
                      })
                    }
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Select
                    label="نوع"
                    value={formData.type}
                    onChange={(e) => {
                      const selectedType = e.target.value as "2d" | "3d";
                      setFormData({ ...formData, type: selectedType });
                      setLastSelectedType(selectedType);
                    }}
                    options={typeOptions}
                  />

                  <NestedSelect
                    label="دسته‌بندی"
                    value={formData.category_id || ""}
                    onChange={(value) => {
                      setFormData({ ...formData, category_id: value });
                      setLastSelectedCategoryId(value);
                    }}
                    options={categories}
                    placeholder="انتخاب دسته‌بندی"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <FileUpload
                      label={editingProduct ? "تغییر تصویر اصلی" : "تصویر اصلی"}
                      value={formFiles.avatar}
                      onChange={(files) =>
                        setFormFiles({ ...formFiles, avatar: files })
                      }
                      multiple={false}
                      maxFiles={1}
                      helperText="برای تغییر، تصویر جدیدی انتخاب کنید"
                    />
                    {editingProduct && editingProduct.avatar && (
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          تصویر اصلی فعلی
                        </label>
                        <img
                          src={getProductImageUrl(
                            editingProduct.id,
                            editingProduct.avatar
                          )}
                          alt="تصویر اصلی فعلی"
                          className="w-32 h-24 object-cover rounded-lg border"
                        />
                      </div>
                    )}
                  </div>

                  <div>
                    <FileUpload
                      label={
                        editingProduct ? "افزودن تصاویر جدید" : "تصاویر گالری"
                      }
                      value={formFiles.photos}
                      onChange={(files) =>
                        setFormFiles({ ...formFiles, photos: files })
                      }
                      multiple={true}
                      maxFiles={10}
                      helperText="تصاویر جدید به گالری اضافه خواهند شد"
                    />
                    {editingProduct &&
                      editingProduct.photos &&
                      editingProduct.photos.length > 0 && (
                        <div className="mb-4">
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            تصاویر گالری فعلی
                          </label>
                          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                            {editingProduct.photos.map((photo, index) => (
                              <div key={index} className="relative group">
                                <img
                                  src={getProductImageUrl(
                                    editingProduct.id,
                                    photo
                                  )}
                                  alt={`تصویر گالری ${index + 1}`}
                                  className="w-full h-24 object-cover rounded-lg border"
                                />
                                <button
                                  type="button"
                                  onClick={() => handleRemovePhoto(photo)}
                                  className="absolute top-1 right-1 bg-red-500 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                                  title="حذف تصویر"
                                >
                                  <Trash2 className="h-3 w-3" />
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                  </div>
                </div>

                <div className="flex justify-end space-x-3 space-x-reverse">
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      setEditingProduct(null);
                      resetForm();
                    }}
                    className="btn-secondary"
                  >
                    انصراف
                  </button>
                  <button
                    type="submit"
                    className="btn-primary"
                    disabled={loading}
                  >
                    {loading
                      ? "در حال ذخیره..."
                      : editingProduct
                      ? "ویرایش"
                      : "افزودن"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductsPage;
